/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Dialog } from '@angular/cdk/dialog';
import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { ConfigService, HistoryStackService, TokenService, ToastService } from '@libs/shared-services';
import { catchError, debounceTime, EMPTY, from, Observable, switchMap, tap } from 'rxjs';
import { ActiveTranscriptionService } from '@libs/speech-recognition';
import { FfdComponentStore, filterWithRemaining, videoMimeTypes } from '@libs/utils';
import {
  CategorizedMessageAttachmentVM,
  CollabMessageAttachment,
  collaborationsActions,
  CollaborationScreenRecordingService,
  ImagingGalleryAttachment,
  MessageHistoryElement,
  MessageInputVM,
} from '@libs/collaboration';
import { EnhanceMessagePromptType, ErrorCode, ImagingCategoryEnum, MessageContentAttachmentMediaTypeEnum } from '@ffd/api-client';
import { fullSeriesTypes, mapImagesToFullSeries, mapMessageAttachmentsToImagingGalleryItems } from '@libs/imaging';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { fromReadableStream } from '../../../../../notes/src/lib/components/notes-screen/notes-utils';

interface MessageInputState {
  allowErrorModal: boolean;
  lastFocusedCursorOffset: number;
  text: string;
  attachments: CategorizedMessageAttachmentVM;
  isGenerating: boolean;
}

const initialState: MessageInputState = {
  allowErrorModal: false,
  lastFocusedCursorOffset: 0,
  text: '',
  attachments: {
    uncategorized: [],
    allAttachments: [],
  },
  isGenerating: false,
};

@Injectable({ providedIn: 'root' })
export class MessageInputService extends FfdComponentStore<MessageInputState, MessageInputVM> {
  public dialog = inject(Dialog);
  private readonly activeTranscriptionService = inject(ActiveTranscriptionService);
  private readonly screenRecordingService = inject(CollaborationScreenRecordingService);
  private readonly historyStackService = inject<HistoryStackService<MessageHistoryElement>>(HistoryStackService);
  private readonly tokenService = inject(TokenService);
  private readonly configService = inject(ConfigService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly ngZone = inject(NgZone);
  private readonly actions$ = inject(Actions);

  private abortController = new AbortController();
  private abortSignal = this.abortController.signal;

  private readonly enhanceRange = signal<{ textStart?: number; textEnd?: number; originalMessage?: string }>({
    textStart: undefined,
    textEnd: undefined,
    originalMessage: undefined,
  });

  // Accumulator for enhanced text chunks during streaming
  private enhancedTextAccumulator = '';

  public allowErrorModalSignal = this.selectSignal((state) => state.allowErrorModal);
  public lastFocusedCursorOffsetSignal = this.selectSignal((state) => state.lastFocusedCursorOffset);
  public readonly messageSignal = this.selectSignal((state) => state.text);
  public readonly recordingStatus = this.screenRecordingService.recordingStatus;
  public readonly isRedoDisabledSignal = computed(() => this.historyStackService.isFirstPosition() || this.state().isGenerating);
  public readonly isUndoDisabledSignal = computed(() => this.historyStackService.isLastPosition() || this.state().isGenerating);

  constructor() {
    super(initialState);
  }

  public override init(): void {
    this.historyStackService.init({ message: '' });
    this.listenToFinishedRecording();
  }

  public readonly setMessage = this.updater(
    (s, message: string): MessageInputState => ({
      ...s,
      text: message,
    }),
  );

  public readonly clearMessageInput = this.updater(
    (): MessageInputState => ({
      ...initialState,
    }),
  );

  private readonly setAttachments = this.updater(
    (s, attachments: CategorizedMessageAttachmentVM): MessageInputState => ({
      ...s,
      attachments,
    }),
  );

  private readonly listenToFinishedRecording = this.effect(($) =>
    $.pipe(
      switchMap(() => this.actions$.pipe(ofType(collaborationsActions.screenRecordingSucceeded))),
      tap(({ file }) => {
        this.addAttachments([file]);
      }),
    ),
  );

  public readonly toggleSpeechRecognitionListening = this.effect(($) =>
    $.pipe(
      tap(() => {
        this.activeTranscriptionService.toggleTranscription({
          onStop: (result) => {
            const selectionStartOffset = this.state().lastFocusedCursorOffset;
            const currentMessageText = this.messageSignal();
            const newNote =
              selectionStartOffset === 0
                ? `${result.transcription}${currentMessageText}`
                : `${currentMessageText?.slice(0, selectionStartOffset)}${result.transcription}${currentMessageText?.slice(
                    selectionStartOffset,
                  )}`;

            this.addToHistoryStack({ message: newNote });
            this.setMessage(newNote);
            this.updateLastFocusedCursorOffset(selectionStartOffset + result.transcription.length);
          },
          onError: (error) => {
            console.error('Voice typing error:', error);
          },
        });
      }),
    ),
  );

  private readonly setAllowErrorModal = this.updater((state, allowErrorModal: boolean): MessageInputState => {
    return { ...state, allowErrorModal };
  });

  public restartSpeechRecognition(): void {
    this.activeTranscriptionService.restartTranscription();
  }

  public selectMicrophone(deviceId: string): void {
    this.activeTranscriptionService.selectMicrophone(deviceId);
  }

  public openAudioInputErrorModal(): void {
    this.activeTranscriptionService.openAudioInputSelectorModal();
  }

  public updateLastFocusedCursorOffset = this.updater((state, lastFocusedCursorOffset: number): MessageInputState => {
    return { ...state, lastFocusedCursorOffset };
  });

  public handleMessageTyping = this.effect<{ text: string }>(($: Observable<{ text: string }>) => {
    return $.pipe(
      debounceTime(500),
      tap(({ text }) => {
        this.addToHistoryStack({
          message: text,
        });
      }),
    );
  });

  public addToHistoryStack = (item: MessageHistoryElement): void => {
    this.historyStackService.push(item);
  };

  public destroy = (): void => {
    this.resetHistory();
    this.clearMessageInput();
  };

  public addAttachments(attachments: File[]): void {
    const mappedAttachments = attachments.map((file) => {
      const url = URL.createObjectURL(file);
      return {
        id: crypto.randomUUID(),
        mediaType: videoMimeTypes.includes(file.type)
          ? MessageContentAttachmentMediaTypeEnum.RECORDING
          : MessageContentAttachmentMediaTypeEnum.IMAGE,
        url,
        file,
        groupLength: 1,
        category: ImagingCategoryEnum.PHOTOS_AND_VIDEOS,
        fullSizeUrl: url,
        thumbnailUrl: url,
      } as ImagingGalleryAttachment;
    });

    this.setAttachments({
      ...this.state().attachments,
      uncategorized: [...this.state().attachments.uncategorized, ...mappedAttachments],
      allAttachments: [...this.state().attachments.allAttachments, ...mappedAttachments],
    });
  }

  public removeAttachment(attachments: CollabMessageAttachment[]): void {
    const allAttachments = this.state().attachments.allAttachments.filter((element) =>
      attachments.every((attachment) => attachment.id !== element.id),
    );
    const [periapicalsAndBitewings] = filterWithRemaining(allAttachments ?? [], (attachment) =>
      fullSeriesTypes.includes(attachment.category),
    );

    const fullSeries = mapImagesToFullSeries(mapMessageAttachmentsToImagingGalleryItems(periapicalsAndBitewings));

    const isFullSeriesFilled = periapicalsAndBitewings.length > 0;

    this.setAttachments({
      ...this.state().attachments,
      fullSeries: isFullSeriesFilled ? fullSeries : undefined,
      allAttachments,
      uncategorized: this.state().attachments.uncategorized.filter((element) =>
        attachments.every((attachment) => attachment.id !== element.id),
      ),
    });
  }

  public addGalleryAttachments(attachments: CollabMessageAttachment[]): void {
    const [periapicalsAndBitewings, others] = filterWithRemaining(attachments ?? [], (attachment) =>
      fullSeriesTypes.includes(attachment.category),
    );

    const mergedAllAttachments = [...this.state().attachments.allAttachments, ...attachments];

    const [statePeriepicalsAndBitewings] = filterWithRemaining(mergedAllAttachments, (attachment) =>
      fullSeriesTypes.includes(attachment.category),
    );

    const mergedPeriapicalsAndBitewings = [
      ...new Map([...periapicalsAndBitewings, ...statePeriepicalsAndBitewings].map((item) => [item.id, item])).values(),
    ];

    const fullSeries = mapImagesToFullSeries<ImagingGalleryAttachment>(
      mapMessageAttachmentsToImagingGalleryItems(mergedPeriapicalsAndBitewings),
    );

    const uncategorized = [...this.state().attachments.uncategorized, ...others];

    this.setAttachments({
      fullSeries: mergedPeriapicalsAndBitewings.length > 0 ? fullSeries : undefined,
      allAttachments: mergedAllAttachments,
      uncategorized: uncategorized.map((item) => ({
        ...item,
        id: `${item.id}`,
      })),
    });
  }

  public undo = (): void => {
    this.historyStackService.undo();
    const newMessage = [...this.historyStackService.stack()][this.historyStackService.position()];
    this.setMessage(newMessage.message);
  };

  public redo = (): void => {
    this.historyStackService.redo();
    this.setMessage(this.historyStackService.getCurrentItem().message);
  };

  public resetHistory = (): void => {
    this.historyStackService.reset();
  };

  private readonly appendEnhancedMessage = (messageChunk: string): void => {
    this.enhancedTextAccumulator += messageChunk;
    this.setMessage(this.enhancedTextAccumulator);
  };

  private readonly appendEnhancedMessageWithTextRange = (messageChunk: string, textStart: number, textEnd: number): void => {
    const enhanceRangeValue = this.enhanceRange();
    const originalMessage = enhanceRangeValue.originalMessage || '';
    this.enhancedTextAccumulator += messageChunk;
    const newMessageText = originalMessage.slice(0, textStart) + this.enhancedTextAccumulator + originalMessage.slice(textEnd);
    this.setMessage(newMessageText);
  };

  public enhanceMessage = this.effect<{
    enhanceType: EnhanceMessagePromptType;
    text?: string;
    textStart?: number;
    textEnd?: number;
    originalMessage?: string;
  }>(
    (
      $: Observable<{
        enhanceType: EnhanceMessagePromptType;
        text?: string;
        textStart?: number;
        textEnd?: number;
        originalMessage?: string;
      }>,
    ) => {
      const encoder = new TextDecoder();
      return $.pipe(
        tap(({ textStart, textEnd, originalMessage }) => {
          this.enhanceRange.set({
            textStart,
            textEnd,
            originalMessage: textStart !== undefined && textEnd !== undefined ? originalMessage : undefined,
          });

          this.enhancedTextAccumulator = '';

          this.patchState({ isGenerating: true });
        }),
        concatLatestFrom(() => [this.tokenService.getAccessToken$({ isUserActivity: true }), this.select((state) => state)]),
        switchMap(([effect, token, message]) => {
          return from(
            fetch(`${this.configService.config()?.apiBasePath}/v1/collaborations/enhance-stream`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                message: effect.text || message.text,
                enhanceType: effect.enhanceType,
              }),
            }),
          ).pipe(
            catchError((error) => {
              console.error('Error while enhancing message', error);
              this.toastService.error(this.translateService.instant('collab_enhance_error_toast_message'));

              this.abortController = new AbortController();
              this.abortSignal = this.abortController.signal;
              this.patchState({ isGenerating: false });
              return EMPTY;
            }),
          );
        }),
        switchMap((response) => {
          if (!response.ok) {
            return from(response.json()).pipe(
              tap((responseJson) => {
                if (
                  response.status === 403 ||
                  (responseJson.statusCode === 400 && responseJson.code === ErrorCode.AI_TOKEN_LIMIT_REACHED)
                ) {
                  this.toastService.error(this.translateService.instant('notes_ai_token_exceeded'));
                }
                this.patchState({ isGenerating: false });
              }),
              switchMap(() => EMPTY),
            );
          }

          return response.body
            ? fromReadableStream(response.body, { ngZone: this.ngZone, signal: this.abortSignal }).pipe(
                tap({
                  next: (chunk) => {
                    const message = encoder.decode(chunk);
                    if (this.enhanceRange().textStart !== undefined && this.enhanceRange().textEnd !== undefined) {
                      this.appendEnhancedMessageWithTextRange(message, this.enhanceRange().textStart!, this.enhanceRange().textEnd!);
                    } else {
                      this.appendEnhancedMessage(message);
                    }
                  },
                  error: () => {
                    console.error('Error while enhancing message');
                    this.toastService.error(this.translateService.instant('collab_enhance_error_toast_message'));
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });
                  },
                  complete: () => {
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });

                    const finalMessage = this.messageSignal();
                    this.addToHistoryStack({
                      message: finalMessage,
                    });

                    this.updateLastFocusedCursorOffset(finalMessage.length);
                  },
                }),
              )
            : EMPTY;
        }),
      );
    },
  );

  protected override mapStateToViewModel(state: MessageInputState): MessageInputVM {
    return {
      text: state.text,
      attachments: state.attachments,
      isGenerating: state.isGenerating,
    };
  }
}
